"use client";

import { type ReactNode, useState } from "react";
import Link from "@/components/locale-link";
import { Button } from "@/components/ui/button";
import type { Dictionary } from "@/lib/i18n";
import { t } from "@/lib/i18n/interpolate";

/**
 * The quiz's strings arrive as a prop rather than through a React context.
 *
 * The generator uses a context because its labels are read nine components deep; this is
 * one component with one consumer, so a prop is the whole mechanism — and `t` comes from
 * `lib/i18n/interpolate` rather than `lib/i18n`, which would pull all seventeen
 * dictionaries into the browser bundle to fill one placeholder.
 */
export type QuizCopy = Dictionary["quizPage"];

interface Question {
  id: string;
  text: string;
  options: { label: string; value: string }[];
}

function questions(copy: QuizCopy): Question[] {
  return [
    {
      id: "discipline",
      text: copy.qDiscipline,
      options: [
        { label: copy.optSocialScience, value: "social-science" },
        { label: copy.optHumanities, value: "humanities" },
        { label: copy.optHistory, value: "history" },
        { label: copy.optMedical, value: "medical" },
        { label: copy.optEngineering, value: "engineering" },
        { label: copy.optNaturalScience, value: "natural-science" },
        { label: copy.optLaw, value: "law" },
        { label: copy.optBusiness, value: "business" },
      ],
    },
    {
      id: "paperType",
      text: copy.qPaperType,
      options: [
        { label: copy.optResearch, value: "research" },
        { label: copy.optReview, value: "review" },
        { label: copy.optReport, value: "report" },
        { label: copy.optEssay, value: "essay" },
        { label: copy.optJournal, value: "journal" },
      ],
    },
    {
      id: "region",
      text: copy.qRegion,
      options: [
        { label: copy.optUs, value: "us" },
        { label: copy.optUk, value: "uk" },
        { label: copy.optCanada, value: "canada" },
        { label: copy.optEurope, value: "europe" },
        { label: copy.optOther, value: "other" },
      ],
    },
    {
      id: "preference",
      text: copy.qPreference,
      options: [
        { label: copy.optAuthorDate, value: "author-date" },
        { label: copy.optNumeric, value: "numeric" },
        { label: copy.optFootnotes, value: "footnotes" },
        { label: copy.optNoPreference, value: "none" },
      ],
    },
  ];
}

interface Recommendation {
  style: string;
  slug: string;
  reason: string;
}

/** The discipline branches, which answer on their own without the other questions. */
function byDiscipline(
  copy: QuizCopy,
  discipline: string,
  preference: string
): Recommendation | null {
  if (discipline === "medical") {
    if (preference === "numeric") {
      return {
        style: copy.styleVancouver,
        slug: "vancouver",
        reason: copy.reasonVancouverMedical,
      };
    }
    return { style: copy.styleAma, slug: "ama", reason: copy.reasonAma };
  }
  if (discipline === "engineering") {
    return { style: copy.styleIeee, slug: "ieee", reason: copy.reasonIeee };
  }
  if (discipline === "law") {
    return {
      style: copy.styleBluebook,
      slug: "chicago",
      reason: copy.reasonBluebook,
    };
  }
  if (discipline === "natural-science") {
    return { style: copy.styleCse, slug: "cse", reason: copy.reasonCse };
  }
  if (discipline === "history") {
    return {
      style: copy.styleChicagoNotes,
      slug: "chicago",
      reason: copy.reasonChicagoHistory,
    };
  }
  if (discipline === "humanities") {
    if (preference === "footnotes") {
      return {
        style: copy.styleChicagoNotes,
        slug: "chicago",
        reason: copy.reasonChicagoHumanities,
      };
    }
    return { style: copy.styleMla, slug: "mla", reason: copy.reasonMla };
  }
  if (discipline === "business") {
    return {
      style: copy.styleApa,
      slug: "apa",
      reason: copy.reasonApaBusiness,
    };
  }
  return null;
}

function getRecommendation(
  copy: QuizCopy,
  answers: Record<string, string>
): Recommendation {
  const { discipline, preference, region } = answers;
  const byField = byDiscipline(copy, discipline, preference);
  if (byField) {
    return byField;
  }

  // Default: social science or unspecified
  if (region === "uk") {
    return {
      style: copy.styleHarvard,
      slug: "harvard",
      reason: copy.reasonHarvard,
    };
  }
  if (preference === "footnotes") {
    return {
      style: copy.styleChicagoNotes,
      slug: "chicago",
      reason: copy.reasonChicagoFootnotes,
    };
  }
  if (preference === "numeric") {
    return {
      style: copy.styleVancouver,
      slug: "vancouver",
      reason: copy.reasonVancouverNumeric,
    };
  }
  return { style: copy.styleApa, slug: "apa", reason: copy.reasonApaDefault };
}

export default function QuizClient({
  copy,
  breadcrumbs,
}: {
  copy: QuizCopy;
  breadcrumbs?: ReactNode;
}) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<Recommendation | null>(null);
  const list = questions(copy);

  function handleAnswer(questionId: string, value: string) {
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);

    if (currentQuestion < list.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setResult(getRecommendation(copy, newAnswers));
    }
  }

  function handleReset() {
    setCurrentQuestion(0);
    setAnswers({});
    setResult(null);
  }

  const question = list[currentQuestion];
  const progress = result ? 100 : (currentQuestion / list.length) * 100;

  return (
    <div className="bg-background">
      <main className="site-container animate-fade-in py-10 md:py-12">
        {breadcrumbs}
        <div className="space-y-10">
          <header className="space-y-4">
            <h1 className="font-semibold text-3xl text-foreground tracking-tight md:text-4xl">
              {copy.heading}
            </h1>
            <p className="text-base text-muted-foreground leading-relaxed md:text-lg">
              {copy.intro}
            </p>
          </header>

          {/* Progress bar */}
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>

          {result ? (
            /* Result */
            <section className="space-y-6">
              <div className="rounded-xl border border-border bg-card p-8 text-center">
                <p className="mb-2 text-muted-foreground text-sm">
                  {copy.recommendLabel}
                </p>
                <h2 className="mb-4 font-semibold text-3xl text-foreground">
                  {result.style}
                </h2>
                <p className="mx-auto max-w-md text-muted-foreground text-sm leading-relaxed">
                  {result.reason}
                </p>
              </div>
              <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
                <Link href={`/${result.slug}`}>
                  <Button>
                    {t(copy.generateButton, { style: result.style })}
                  </Button>
                </Link>
                <Button onClick={handleReset} variant="outline">
                  {copy.retakeButton}
                </Button>
              </div>
              <div className="text-center">
                <Link
                  className="text-muted-foreground text-sm underline hover:text-foreground"
                  href="/guides"
                >
                  {copy.guidesLink}
                </Link>
              </div>
            </section>
          ) : (
            /* Question */
            <section className="space-y-6">
              <div className="space-y-2">
                <p className="text-muted-foreground text-sm">
                  {t(copy.progress, {
                    current: currentQuestion + 1,
                    total: list.length,
                  })}
                </p>
                <h2 className="font-semibold text-foreground text-xl">
                  {question.text}
                </h2>
              </div>
              <div className="grid gap-3">
                {question.options.map((option) => (
                  <button
                    className="rounded-lg border border-border bg-card p-4 text-left text-foreground text-sm transition-colors hover:border-foreground/20 hover:bg-accent"
                    key={option.value}
                    onClick={() => handleAnswer(question.id, option.value)}
                    type="button"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {currentQuestion > 0 && (
                <Button
                  onClick={() => setCurrentQuestion(currentQuestion - 1)}
                  size="sm"
                  variant="ghost"
                >
                  {copy.previousButton}
                </Button>
              )}
            </section>
          )}
        </div>
      </main>
    </div>
  );
}
